import { BillingAccountBlock } from "@/admin/accounts/BillingAccountBlock.tsx";
import type { GQLoadBalancerItem } from "@/admin/load_balancers/LoadBalancersTable.tsx";
import { ClipBoardTooltip } from "@/components/ClipBoardTooltip.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow } from "@/components/table/DetailsTableRow.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { getResourceDescription } from "@/modules/api/resourceTypes.tsx";
import { LocationDetail } from "@/modules/location/LocationDetail.tsx";
import { convertTargetType } from "@/modules/network/loadbalancer/loadBalancer.logic.ts";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { LbTargetType } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";

export function LoadBalancerDetailView({ item }: { item: GQLoadBalancerItem }) {
    return (
        <>
            <LoadBalancerDetails item={item} />
            <Separator />
            <ContentPane className="VStack gap-4">
                <SectionHeader title="Description" />
                <div>{getResourceDescription("load_balancer")}</div>
            </ContentPane>
            <Separator />
            <TargetServersContent obj={item} />
            <Separator />
            <ForwardingRulesContent obj={item} />
        </>
    );
}

function LoadBalancerDetails({ item }: { item: GQLoadBalancerItem }) {
    const { uuid, display_name, private_address, _location, billing_account } = item;

    return (
        <ContentPane className="VStack gap-4">
            <ViewTitle title={display_name ?? ""} />

            <DetailsHolder>
                <DetailsTable>
                    <DetailsTableRow title="Name">{display_name}</DetailsTableRow>

                    <DetailsTableRow title="Type">
                        <ResourceWithIcon type="load_balancer" />
                    </DetailsTableRow>

                    <DetailsTableRow title="Private IPv4">{private_address}</DetailsTableRow>
                </DetailsTable>

                <Spacer />

                <DetailsTable>
                    <DetailsTableRow title="UUID">
                        <ClipBoardTooltip>{uuid}</ClipBoardTooltip>
                    </DetailsTableRow>

                    <DetailsTableRow title="Created">
                        <LongDate date={apiUnixToDate(item.created_at as number)} />
                    </DetailsTableRow>

                    <LocationDetail slug={notNull(_location)} />

                    <DetailsTableRow title="Billing account">
                        <BillingAccountBlock showId value={billing_account} />
                    </DetailsTableRow>
                </DetailsTable>
            </DetailsHolder>
        </ContentPane>
    );
}

function TargetServersContent({ obj }: { obj: GQLoadBalancerItem }) {
    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title="Target servers" />

            <WTable>
                <thead>
                    <tr>
                        <th>UUID</th>
                        <th>Type</th>
                        <th>Private IP</th>
                        <th>Created</th>
                    </tr>
                </thead>
                <WTableBody>
                    {obj.targets.map((item) => {
                        return (
                            <tr key={item.target_uuid}>
                                <td>
                                    <ClipBoardTooltip>{item.target_uuid}</ClipBoardTooltip>
                                </td>
                                <td>
                                    <ResourceWithIcon type={convertTargetType(item.target_type as LbTargetType)} />
                                </td>
                                <td>
                                    <ClipBoardTooltip>{item.target_ip_address}</ClipBoardTooltip>
                                </td>
                                <td>
                                    <LongDate date={apiUnixToDate(item.created_at as number)} />
                                </td>
                            </tr>
                        );
                    })}
                </WTableBody>
            </WTable>
        </ContentPane>
    );
}

function ForwardingRulesContent({ obj }: { obj: GQLoadBalancerItem }) {
    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title="Forwarding rules" />

            <WTable>
                <thead>
                    <tr>
                        <th>UUID</th>
                        <th>Protocol</th>
                        <th className="text-right">Port</th>
                        <th />
                        <th>Target port</th>
                        <th>Settings</th>
                        <th>Created</th>
                    </tr>
                </thead>
                <WTableBody>
                    {obj.forwarding_rules.map((item) => (
                        <tr key={item.uuid}>
                            <td>
                                <ClipBoardTooltip>{item.uuid}</ClipBoardTooltip>
                            </td>
                            <td>TCP</td>
                            <td className="text-right">{item.source_port}</td>
                            <td className="text-center">
                                <MaskIcon className="jp-arrow-thin-right-icon size-1.25rem color-muted" />
                            </td>
                            <td>{item.target_port}</td>
                            <td>
                                Connection limit: {item.settings.connection_limit}
                                <br />
                                Session persistence: {item.settings.session_persistence}
                            </td>
                            <td>
                                <LongDate date={apiUnixToDate(item.created_at as number)} />
                            </td>
                        </tr>
                    ))}
                </WTableBody>
            </WTable>
        </ContentPane>
    );
}
